import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useDeleteChartOfAccount,
  useFinanceSettingsStore,
  useUpdateChartOfAccount
} from "features/finances";
import moment from "moment";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import * as yup from "yup";
import { EthiopianDateField } from "../EthiopianDateField";
import { ValidationError } from "../ValidationError";
import { BaseModal } from "./BaseModal";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
  background-color: transparent;
`;

const chartOfAccountSchema = yup.object({
  accountNumber: yup.string().required("Account Number is required"),
  accountName: yup.string().required("Account Name is required"),
  accountType: yup.string().required("Account Type is required"),
  description: yup.string().required("Description is required"),
  beginningBalance: yup
    .number()
    .typeError("Beginning Balance must be a number")
    .required("Beginning Balance is required"),
  beginningBalanceDate: yup
    .string()
    .required("Beginning Balance Date is required")
});

export function UpdateChartOfAccountModal({ data }) {
  const updateChartOfAccountOpen = useFinanceSettingsStore(
    state => state.updateChartOfAccountOpen
  );
  const closeUpdateChartOfAccountModal = useFinanceSettingsStore(
    state => state.closeUpdateChartOfAccountModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const updateChartOfAccountMutation = useUpdateChartOfAccount();
  const deleteChartOfAccountMutation = useDeleteChartOfAccount();

  const accountTypes = ["ASSET", "LIABILITY", "EQUITY", "INCOME", "EXPENSE"];

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control
  } = useForm({
    defaultValues: {
      accountNumber: data?.account_number || "",
      accountName: data?.account_name || "",
      accountType: data?.account_type || "",
      description: data?.description || "",
      beginningBalance: data?.beginning_balance || 0,
      beginningBalanceDate:
        moment(data?.beginning_balance_date).format("YYYY-MM-DD") || ""
    },
    resolver: yupResolver(chartOfAccountSchema)
  });

  const beginningBalanceDate = watch("beginningBalanceDate");

  useEffect(() => {
    if (data) {
      setValue("accountNumber", data?.account_number || "");
      setValue("accountName", data?.account_name || "");
      setValue("accountType", data?.account_type || "");
      setValue("description", data?.description || "");
      setValue("beginningBalance", data?.beginning_balance || 0);
      setValue(
        "beginningBalanceDate",
        moment(data?.beginning_balance_date).format("YYYY-MM-DD") || ""
      );
    }
  }, [data]);

  const onSubmit = values => {
    updateChartOfAccountMutation.mutate(
      {
        churchId: selectedChurch?.id,
        chartOfAccountId: data?.id,
        chartOfAccountData: {
          account_number: values.accountNumber,
          account_name: values.accountName,
          account_type: values.accountType,
          description: values.description,
          beginning_balance: values.beginningBalance,
          beginning_balance_date: values.beginningBalanceDate
        }
      },
      {
        onSuccess: () => {
          reset();
        }
      }
    );
  };

  return (
    <BaseModal
      title="Update Chart of Account"
      isOpen={updateChartOfAccountOpen}
      onClose={closeUpdateChartOfAccountModal}
      footer={
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Button
            flavor="neutral"
            size="small"
            onClick={() => {
              deleteChartOfAccountMutation.mutate({
                churchId: selectedChurch?.id,
                chartOfAccountId: data?.id
              });
            }}
            disabled={
              deleteChartOfAccountMutation.isLoading ||
              updateChartOfAccountMutation.isLoading
            }
          >
            Delete
          </Button>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              flavor="white"
              size="small"
              onClick={closeUpdateChartOfAccountModal}
              disabled={
                deleteChartOfAccountMutation.isLoading ||
                updateChartOfAccountMutation.isLoading
              }
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={handleSubmit(onSubmit)}
              disabled={
                deleteChartOfAccountMutation.isLoading ||
                updateChartOfAccountMutation.isLoading
              }
            >
              Save Changes
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Field>
        <Label htmlFor="accountNumber">Account ID</Label>
        <Input id="accountNumber" {...register("accountNumber")} />
        <ErrorMessage
          errors={errors}
          name="accountNumber"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="accountName">Account Name</Label>
        <Input id="accountName" {...register("accountName")} />
        <ErrorMessage
          errors={errors}
          name="accountName"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="accountType">Account Type</Label>
        <Select id="accountType" {...register("accountType")}>
          <option value="" disabled>
            Select account type
          </option>
          {accountTypes.map(accountType => (
            <option key={accountType} value={accountType}>
              {accountType}
            </option>
          ))}
        </Select>
        <ErrorMessage
          errors={errors}
          name="accountType"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="description">Description</Label>
        <Input id="description" {...register("description")} />
        <ErrorMessage
          errors={errors}
          name="description"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="beginningBalance">Beginning Balance</Label>
        <Controller
          name="beginningBalance"
          control={control}
          render={({ field }) => (
            <NumericFormat
              customInput={Input}
              id="beginningBalance"
              thousandSeparator
              allowNegative={false}
              onValueChange={values => {
                field.onChange(values.floatValue);
              }}
              value={field.value}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="beginningBalance"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="beginningBalanceDate">Beginning Balance Date</Label>
        <EthiopianDateField
          size="small"
          value={beginningBalanceDate}
          onChange={e => setValue("beginningBalanceDate", e.target.value)}
        />
        <ErrorMessage
          errors={errors}
          name="beginningBalanceDate"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>

      <MutationError
        error={updateChartOfAccountMutation.error}
        sx={{ mt: 2 }}
      />
    </BaseModal>
  );
}
